import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { companyContextApi } from '../services/companyContextApi';
import type { CompanyContext, AutoGenerateStatus } from '../../../types/companyContext';

const CONTEXT_KEY = 'company-context';
const INTERVIEW_KEY = 'company-context-interview';
const AUTO_GENERATE_STATUS_KEY = 'company-context-auto-generate-status';

export function useCompanyContext() {
  const queryClient = useQueryClient();
  const prevStatusRef = useRef<string | undefined>();

  // ── Queries ────────────────────────────────────────────────

  const contextQuery = useQuery({
    queryKey: [CONTEXT_KEY],
    queryFn: companyContextApi.getContext,
    staleTime: 60_000,
  });

  const interviewQuery = useQuery({
    queryKey: [INTERVIEW_KEY],
    queryFn: companyContextApi.getInterviewOutput,
    staleTime: 60_000,
    retry: false, // Don't retry on 404 (no interview done yet)
  });

  const autoGenerateStatusQuery = useQuery({
    queryKey: [AUTO_GENERATE_STATUS_KEY],
    queryFn: companyContextApi.getAutoGenerateStatus,
    staleTime: 60_000,
    refetchInterval: (query) => {
      const status = query.state.data as AutoGenerateStatus | undefined;
      return status?.status === 'in_progress' ? 5_000 : false;
    },
  });

  // Auto-refetch the main context when auto-generate completes so the
  // form fields get populated without a manual page reload.
  const currentStatus = autoGenerateStatusQuery.data?.status;
  useEffect(() => {
    if (prevStatusRef.current === 'in_progress' && currentStatus === 'completed') {
      queryClient.invalidateQueries({ queryKey: [CONTEXT_KEY] });
    }
    prevStatusRef.current = currentStatus;
  }, [currentStatus, queryClient]);

  // ── Mutations ──────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: (context: Partial<CompanyContext>) => companyContextApi.saveContext(context),
    onSuccess: (data) => {
      queryClient.setQueryData([CONTEXT_KEY], data);
      queryClient.invalidateQueries({ queryKey: [CONTEXT_KEY] });
      // `agentesApi` (44 kB de fonte) só é preciso aqui, depois de salvar: importá-lo
      // estaticamente pôs o módulo inteiro na primeira pintura (cabeçalho -> IcpPopover -> este hook).
      void import('../../agentes/services/agentesApi')
        .then((m) => m.agentesApi.syncCompanyContext())
        .then(() => {
          void queryClient.invalidateQueries({ queryKey: ['agentes'] });
        })
        .catch(() => {
          // Silencioso se o tenant nao tiver agente ativo ainda
        });
      message.success('Contexto da empresa salvo com sucesso!');
    },
    onError: () => {
      message.error('Erro ao salvar contexto da empresa.');
    },
  });

  const autoGenerateMutation = useMutation({
    mutationFn: ({ companyName, domain }: { companyName: string; domain?: string }) =>
      companyContextApi.triggerAutoGenerate(companyName, domain),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AUTO_GENERATE_STATUS_KEY] });
      message.success('Pesquisa automatica iniciada!');
    },
    onError: () => {
      message.error('Erro ao iniciar pesquisa automatica.');
    },
  });

  return {
    context: contextQuery.data,
    interviewOutput: interviewQuery.data,
    // Don't block the page on interview query — it returns 404 for new tenants
    isLoading: contextQuery.isLoading,
    save: saveMutation.mutate,
    isSaving: saveMutation.isPending,
    triggerAutoGenerate: autoGenerateMutation.mutate,
    autoGenerateStatus: autoGenerateStatusQuery.data,
    isAutoGenerating: autoGenerateMutation.isPending,
    refetch: contextQuery.refetch,
  };
}
